import * as React from 'react'

import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { type Users } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import type { Employees } from 'modules/employees/store/collections/employees'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { usersWithRegularAccess } from 'modules/surveys/lib/usersWithRegularAccess'

interface Props {
  employees: Employees
  users: Users
  members: number[]
  setMembers: (ids: number[]) => void
  everybody?: boolean
  allMyDescendants: number[]
}

export function computeAvailableUsers(
  users: Users,
  employees: Employees,
  userId: number,
  everybody = false,
  allMyDescendants: number[],
  canCreateEverybody: boolean,
  canCreateReportees: boolean,
  canCreateTeam: boolean,
  canCreateOwned: boolean
): User[] {
  if (everybody || canCreateEverybody) {
    return usersWithRegularAccess(users, employees)
  }

  let list = [users.current]

  const currentUser = users.current
  const currentEmployee = currentUser.isNew
    ? undefined
    : employees.find({
        access_id: currentUser.attributes.id,
      })

  if (!currentEmployee) return list

  if (canCreateReportees) {
    const reportingToMeDirectly = employees.filter(
      (employee) => employee.attributes.manager_id === currentEmployee.id
    )

    list = list.concat(
      reportingToMeDirectly.map((e) => {
        const accessId = e.attributes.access_id
        const user = users.get(accessId)
        if (!user) {
          throw Error(`Error getting users with id = ${accessId}`)
        }
        return user
      })
    )
  }

  if (canCreateTeam) {
    const reportingToMe = employees.filter((employee) => allMyDescendants.includes(employee.id))

    list = list.concat(
      reportingToMe.map((e) => {
        const accessId = e.attributes.access_id
        const user = users.get(accessId)
        if (!user) {
          throw Error(`Error getting users with id = ${accessId}`)
        }
        return user
      })
    )
  }

  if (!canCreateOwned) {
    list = list.filter((user) => user.id !== userId)
  }

  list = list.filter((user) => {
    const employee = user.isNew
      ? undefined
      : employees.find({
          access_id: user.attributes.id,
        })
    return hasRegularAccess(!!employee?.attributes.has_regular_access) || !employee
  })

  return [...new Set(list)]
}

function ManualSelection({ employees, users, members, setMembers, everybody = false }: Props) {
  const { canCreateEverybody, canCreateReportees, canCreateTeam, canCreateOwned } =
    useEngagementContext()
  const { allMyDescendants } = useCurrentSession()
  const _setMembers = (ids: number[]) => setMembers(ids)

  const {
    access: { id: userId },
  } = useCurrentSession()
  const setMember = (id: number | null | undefined) => {
    if (!id) return setMembers([])

    setMembers(xor(members, [id]))
  }

  // This is a bit of an expensive computation, so we are memoizing it at the
  // expense of less reactivity.
  const availableUsers = React.useRef(
    computeAvailableUsers(
      users,
      employees,
      userId,
      everybody,
      allMyDescendants,
      canCreateEverybody,
      canCreateReportees,
      canCreateTeam,
      canCreateOwned
    )
  ).current

  return (
    <UserSelector
      users={availableUsers}
      onSelect={setMember}
      onSelectMultiple={_setMembers}
      selectedIds={members}
      hideFields={[Fields.EmployeeStatus, Fields.UserType]}
    />
  )
}

export default observer(ManualSelection)
